import { useState, useEffect } from 'react'
import s from './Details.scss'
import { isAdmin } from 'utils/authService'
import {
  KeyValuePairs,
  InputContainer,
  GoogleAutocomplete,
  AddOptions
} from 'components/ideal-core-ui'
import { FaInfoCircle } from 'react-icons/fa'
// import { SIZES } from './constants'
import Select from 'react-select'
import cn from 'classnames'
import { APP_CONSTANTS } from 'utils/constants'
import {
  fromConstantPropertyToSelectValue,
  fromConstantPropertyToSelectOptions
} from 'utils/serializers'
import { Row, Col, FormGroup, Label, Input, Button, UncontrolledTooltip } from 'reactstrap'
import { useImmer } from 'use-immer'
import { makeGetRequest } from 'utils/api'
import moment from 'moment-timezone'

const Details = ({ details, /* setSize, */ saveDetails }) => {
  const [timezones, setTimezones] = useState([])
  const [organizationDetails, setOrganizationDetails] = useImmer(details)
  const {
    name,
    shortName,
    /* size, */ type,
    interest,
    departments,
    timezone,
    phone,
    address,
    address2,
    street2,
    city,
    state,
    zipcode
  } = organizationDetails
  useEffect(() => {
    setOrganizationDetails(() => {
      return details
    })
  }, [details, setOrganizationDetails])

  useEffect(() => {
    ;(async () => {
      const { timezones } = await makeGetRequest('/v1/timezones')
      setTimezones(
        timezones.map(r => ({
          value: r,
          label: `${r} (current ${moment().tz(r)})`
        }))
      )
    })()
  }, [])

  const canSave = () => {
    if (name && shortName /* && size */ && type && interest && departments.length) return false
    return true
  }

  const changeProperty = (value, property) => {
    setOrganizationDetails(state => {
      state[property] = value
    })
  }

  const changeProperties = (values, page, properties) => {
    setOrganizationDetails(state => {
      properties.forEach((prop, index) => {
        state[prop] = values[index]
      })
    })
  }

  return (
    <>
      <KeyValuePairs
        pairs={[
          {
            key: '* Company Name',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <InputContainer
                type="text"
                placeholder="Enter Company Name"
                cb={value => changeProperty(value, 'name')}
                text={name || ''}
                cy="company_name_input"
              />
            )
          },
          {
            key: (
              <span>
                * Short Name
                <FaInfoCircle id="Type1" />
                <UncontrolledTooltip placement="top" target="Type1">
                  Your company short name has to be unique and will be used to create a universal
                  email address. It will not be visible to your clients.
                </UncontrolledTooltip>
              </span>
            ),
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <InputContainer
                type="text"
                placeholder="Enter Company Name"
                cb={value => {
                  changeProperties(
                    [value, `${value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}@idealemail.app`],
                    'details',
                    ['shortName', 'email']
                  )
                }}
                text={shortName || ''}
                cy="company_short_name_input"
              />
            )
          },
          {
            key: (
              <span>
                Assigned universal email address
                <FaInfoCircle id="Type2" />
                <UncontrolledTooltip placement="top" target="Type2">
                  When you BCC your universal email address we will capture those email
                  communications and save them in the iDeal CRM system.
                </UncontrolledTooltip>
              </span>
            ),
            keyClass: [s.label],
            valueClass: [s.email],
            value: `${
              shortName
                ? shortName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() + '@idealemail.app'
                : ''
            }`
          },
          // {
          //   key: '* How many people will use iDeal CRM?',
          //   keyClass: [s.label],
          //   valueClass: [s.value],
          //   value: (
          //     <div className={s.sizeWapper}>
          //       {SIZES.map(item => (
          //         <div
          //           key={item.value}
          //           onClick={() => {
          //             changeProperty(item.value, 'size')
          //             setSize(item.value)
          //           }}
          //           className={cn(s.size, {
          //             [s.active]: size === item.value
          //           })}
          //         >{item.label}</div>
          //       ))}
          //     </div>
          //   )
          // },
          {
            key: '* Company Type',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <Select
                value={type ? fromConstantPropertyToSelectValue(type, 'OrganizationType') : ''}
                placeholder="Select Company Type"
                onChange={value => changeProperty(value.value, 'type')}
                options={fromConstantPropertyToSelectOptions(
                  Object.keys(APP_CONSTANTS.OrganizationType),
                  'OrganizationType'
                )}
                id="company_type_select_cy"
              />
            )
          },
          {
            key: '* What problem are you here to solve?',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <Select
                value={
                  interest
                    ? fromConstantPropertyToSelectValue(interest, 'OrganizationInterest')
                    : ''
                }
                placeholder="Select Company Interest"
                onChange={value => changeProperty(value.value, 'interest')}
                options={fromConstantPropertyToSelectOptions(
                  Object.keys(APP_CONSTANTS.OrganizationInterest),
                  'OrganizationInterest'
                )}
                id="company_interest_select_cy"
              />
            )
          },
          {
            key: '* Timezone',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <Select
                value={timezone}
                placeholder="Select Timezone"
                onChange={value => changeProperty(value, 'timezone')}
                options={timezones}
                id="company_timezone_cy"
              />
            )
          },
          {
            key: 'Phone',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <InputContainer
                type="text"
                placeholder="Enter Phone Number"
                cb={value => changeProperty(value, 'phone')}
                text={phone || ''}
                cy="company_phone_input"
              />
            )
          },
          {
            key: '* Departments',
            keyClass: [s.label],
            valueClass: [s.value],
            value: (
              <AddOptions
                items={departments}
                editUrl="/v1/departments"
                toItemSerializer={item => item}
                addItem={(departments, departmentName) => {
                  changeProperty(
                    [...departments, { value: departmentName, label: departmentName }],
                    'departments'
                  )
                }}
                deleteItem={(department, departments) => {
                  const index = departments.findIndex(d => d === department)
                  const tmp = [...departments]
                  tmp.splice(index, 1)
                  changeProperty(tmp, 'departments')
                }}
                cy="company_departments"
              />
            )
          }
        ]}
      />
      {isAdmin() && (
        <Button
          disabled={canSave()}
          className={cn(s.greenBtn, s.saveBtn)}
          onClick={() => saveDetails(organizationDetails)}
        >
          Save
        </Button>
      )}
      <div className={s.underlineTitle}>
        <span>Location</span>
      </div>
      <FormGroup>
        <div className={s.fieldWrapper}>
          <Label className={s.formLabel} for="address">
            Address
          </Label>
          <GoogleAutocomplete
            className={s.googleAutocomplete}
            value={address || ''}
            name="address"
            onChange={e => changeProperty(e, 'address')}
            onSelectCallback={value =>
              changeProperties(
                [value.address, value.city, value.state, value.street, value.zipCode],
                'details',
                ['address', 'city', 'state', 'address2', 'zipcode']
              )
            }
          />
        </div>
        <div className={s.fieldWrapper}>
          <Label className={s.formLabel} for="address2">
            Street 1
          </Label>
          <Input
            type="text"
            onChange={({ target }) => changeProperty(target.value, 'address2')}
            value={address2 || ''}
            name="address2"
            placeholder="Enter Street Address"
          />
        </div>
        <div className={s.fieldWrapper}>
          <Label className={s.formLabel} for="street2">
            Street 2
          </Label>
          <Input
            type="text"
            onChange={({ target }) => changeProperty(target.value, 'street2')}
            value={street2 || ''}
            name="street2"
            placeholder="Enter Street Address"
          />
        </div>
        <Row className={s.rowForm}>
          <Col className={s.colForm} lg="4">
            <Label className={s.formLabel} for="city">
              City
            </Label>
            <Input
              type="text"
              onChange={({ target }) => changeProperty(target.value, 'city')}
              value={city || ''}
              name="city"
              placeholder="Enter City"
            />
          </Col>
          <Col className={s.colForm} lg="4">
            <Label className={s.formLabel} for="state">
              State/Province/Region
            </Label>
            <Input
              type="text"
              onChange={({ target }) => changeProperty(target.value, 'state')}
              value={state || ''}
              name="state"
              placeholder="Enter State/Province/Region"
            />
          </Col>
          <Col className={s.colForm} lg="4">
            <Label className={s.formLabel} for="zipcode">
              Zip/Postal Code
            </Label>
            <Input
              type="text"
              onChange={({ target }) => changeProperty(target.value, 'zipcode')}
              value={zipcode || ''}
              name="zipcode"
              placeholder="Enter Zip/Postal Code"
            />
          </Col>
        </Row>
      </FormGroup>
      {isAdmin() && (
        <Button
          disabled={canSave()}
          className={cn(s.greenBtn, s.saveBtn)}
          onClick={() => saveDetails(organizationDetails)}
          data-cy="submit_company_btn"
        >
          Save
        </Button>
      )}
    </>
  )
}

export default Details
